import type { FC } from "react"
import { useMemo } from "react"

import { ALL_WAREHOUSES_OPTION, ALL_WAREHOUSES_VALUE } from "../../constants"

import { SpecValue } from "./SpecValue"

import { NoValuePlaceholder } from "@/components/common/NoValuePlaceholder"
import { AerosSelect } from "@/components/UiKit"
import type { InventoryByProductItem } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"
import { formatNumber } from "@/utils/number"
import { getWarehouseOptions } from "@/utils/warehouse"

interface ProductInventoryStatsProps {
  inventory: InventoryByProductItem
  warehouseAreaId?: string
  onWarehouseChange: (id: string) => void
}

export const ProductInventoryStats: FC<ProductInventoryStatsProps> = ({
  inventory,
  warehouseAreaId,
  onWarehouseChange,
}) => {
  const warehouseOptions: Option<string>[] = useMemo(
    () => [
      ALL_WAREHOUSES_OPTION,
      ...getWarehouseOptions((inventory?.warehouses ?? []).map(({ warehouseArea }) => warehouseArea!)),
    ],
    [inventory]
  )

  const warehouseInventory =
    warehouseAreaId === ALL_WAREHOUSES_VALUE
      ? null
      : inventory?.warehouses.find(
          (warehouseInventory) => warehouseInventory.warehouseArea?.warehouseAreaId === warehouseAreaId
        )

  const qtyOnHand = warehouseInventory?.qtyOnHand ?? inventory?.qtyOnHand ?? 0
  const availableQty = warehouseInventory?.availableQty ?? inventory?.availableQty ?? 0
  const softAllocatedQty = warehouseInventory?.softAllocatedQty ?? inventory?.softAllocatedQty ?? 0
  const hardAllocatedQty = warehouseInventory?.hardAllocatedQty ?? inventory?.hardAllocatedQty ?? 0
  const balToCome = warehouseInventory?.balToCome ?? inventory?.balToCome ?? 0
  const moh = warehouseInventory?.moh ?? inventory?.moh ?? 0

  return (
    <div className='flex items-stretch justify-between gap-6 rounded-md border border-solid border-gray-400 px-6 py-4'>
      <AerosSelect
        options={warehouseOptions}
        value={warehouseAreaId ?? ALL_WAREHOUSES_VALUE}
        onChange={(value) => onWarehouseChange(value ?? ALL_WAREHOUSES_VALUE)}
        placeholder='Select warehouse'
        className='w-[280px]'
        data-testid='product-inventory.stats.warehouse-select'
      />
      <SpecValue label='Total (lbs)' value={formatNumber(qtyOnHand)} />
      <SpecValue label='Available' value={formatNumber(availableQty)} />
      <SpecValue label='Soft Alloc' value={formatNumber(softAllocatedQty)} />
      <SpecValue label='Hard Alloc' value={formatNumber(hardAllocatedQty)} />
      <SpecValue label='Bal to Come' value={formatNumber(balToCome)} />
      <SpecValue label='MOH/w Orders' value={formatNumber(moh)} />
      <SpecValue label='Sales avg 3 Mo. / 12 Mo.' value={<NoValuePlaceholder />} />
      <SpecValue label='Sales YTD' value={<NoValuePlaceholder />} />
      <SpecValue label='Sales 12 Mo.' value={<NoValuePlaceholder />} />
    </div>
  )
}
